import {
  call,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'typed-redux-saga/macro';

import * as govQAAPI from '@helpers/govQAAPI';
import * as foiaXpressAPI from '@helpers/foiaXpressAPI';
import * as casepointAPI from '@helpers/casepointAPI';
import * as exterroAPI from '@helpers/exterroAPI';
import * as nuixAPI from '@helpers/nuixAPI';
import * as externalIntegrationAPI from '@helpers/externalIntegrationApi';
import type { ExternalIntegrationConfig } from '@helpers/externalIntegrationApi';

import {
  FETCH_EXTERNAL_INTEGRATION_CONFIG,
  PATCH_INTEGRATION_STATE,
  SEND_TO_CASEPOINT,
  SEND_TO_EXTERRO,
  SEND_TO_EXTERNAL_INTEGRATION,
  SEND_TO_FOIAXPRESS,
  SEND_TO_GOVQA,
  SEND_TO_NUIX,
} from '../actions';
import { selectSession } from '../selectors';
import {
  IntegrationAuth,
  IntegrationKey,
  IntegrationState,
  RedactedVersion,
  SessionState,
} from '../models';

/**
 * VE-23442 — "Send to" external integrations for the document editor.
 *
 * Ports the six mediaDetails redacted-files "Send to" flows (GovQA,
 * FOIAXpress, Casepoint, Exterro, Nuix, and the generic org-configured
 * "external integration") to the documents module. The actual transport is
 * the shared `src/helpers/*API.ts` helpers (also used by the video flow); this
 * saga just orchestrates: resolve the latest redacted file from the session,
 * fetch the org's integration config, call the helper, and reflect
 * success/error into the per-integration UI state.
 *
 * Unlike the mediaDetails sagas, the redacted file + audit-log signed URLs are
 * already in `session.versionHistory` (populated at open / after burn-in), so
 * there's no TDO/audit-log re-fetch round-trip here.
 */

/** File descriptor accepted by every helper's `sendFile`. */
interface IntegrationFile {
  name: string;
  signedUri: string;
  contentType: string;
}

/** `PATCH_INTEGRATION_STATE` shorthand. */
function patch(key: IntegrationKey, p: Partial<IntegrationState>) {
  return PATCH_INTEGRATION_STATE({ key, patch: p });
}

/** Latest redacted version (newest is last — see openDocument's reverse()). */
function latestVersion(session: SessionState): RedactedVersion | null {
  return session.versionHistory[session.versionHistory.length - 1] ?? null;
}

/** Build the redacted-PDF file descriptor; null when its URL isn't ready. */
function buildMediaFile(
  latest: RedactedVersion,
  documentName: string
): IntegrationFile | null {
  if (!latest.redactedAssetUrl) {
    return null;
  }
  return {
    name: latest.name ?? documentName,
    signedUri: latest.redactedAssetUrl,
    contentType: 'application/pdf',
  };
}

/** Build the audit-log file descriptor, or undefined when none exists. */
function buildAuditLog(
  latest: RedactedVersion,
  documentName: string
): IntegrationFile | undefined {
  if (!latest.auditLogUrl) {
    return undefined;
  }
  // The helpers suffix the first dot-segment with `_redactionlog`; give the
  // audit log a `.csv` base name so the result keeps a sensible extension.
  const base = latest.name ?? documentName;
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return {
    name: `${stem}.csv`,
    signedUri: latest.auditLogUrl,
    contentType: 'text/csv',
  };
}

/**
 * Either a flat key/value vendor config (GovQA/FOIAXpress/…) or the generic
 * external-integration template config. Read with a cast at each call site.
 */
type FetchedConfig = Record<string, string> | ExternalIntegrationConfig;

/** Shape of a `fetchIntegrationInfo` SUCCESS action's payload. */
interface IntegrationInfoSuccessAction {
  payload?: {
    me?: {
      organization?: {
        integrationConfig?: { config?: FetchedConfig } | null;
      } | null;
    } | null;
  } | null;
}

type TakePattern = Parameters<typeof take>[0];

/**
 * Dispatch a helper's `fetchIntegrationInfo` thunk and await its
 * SUCCESS/FAILURE. Returns the `integrationConfig.config` payload, or null on
 * failure / missing config.
 */
function* fetchConfig(
  thunk: () => unknown,
  successType: TakePattern,
  failureType: TakePattern
): Generator<unknown, FetchedConfig | null, unknown> {
  // `fetchIntegrationInfo` is a redux-thunk; `put` dispatches it through the
  // thunk middleware (it isn't a plain action, hence the cast).
  yield* put(thunk() as ReturnType<typeof PATCH_INTEGRATION_STATE>);
  const { ok, err } = yield* race({
    ok: take(successType),
    err: take(failureType),
  });
  if (err || !ok) {
    return null;
  }
  const action = ok as IntegrationInfoSuccessAction;
  return action.payload?.me?.organization?.integrationConfig?.config ?? null;
}

/**
 * Shared prologue: flag the integration as sending and resolve the redacted
 * file + audit log. Returns null (after clearing the spinner) when there's no
 * sendable redacted file yet.
 */
function* beginSend(key: IntegrationKey): Generator<
  unknown,
  {
    session: SessionState;
    latest: RedactedVersion;
    mediaFile: IntegrationFile;
    auditLog?: IntegrationFile;
  } | null,
  any
> {
  yield* put(patch(key, { isSendingFile: true }));
  const session = yield* select(selectSession);
  if (!session) {
    yield* put(patch(key, { isSendingFile: false }));
    return null;
  }
  const latest = latestVersion(session);
  const mediaFile = latest && buildMediaFile(latest, session.documentName);
  if (!latest || !mediaFile) {
    console.error(`sendTo[${key}]: no redacted file with a signed URL to send`);
    yield* put(patch(key, { isSendingFile: false }));
    return null;
  }
  const auditLog = buildAuditLog(latest, session.documentName);
  return { session, latest, mediaFile, auditLog };
}

/** Show an error in the integration's status dialog and stop the spinner. */
function* failSend(key: IntegrationKey, message: string) {
  yield* put(
    patch(key, {
      successMessage: '',
      isStatusDialogShown: true,
      errorMessage: message,
    })
  );
  yield* put(patch(key, { isSendingFile: false }));
}

/** Show a success message in the status dialog and stop the spinner. */
function* succeedSend(key: IntegrationKey, message: string) {
  yield* put(
    patch(key, {
      successMessage: message,
      isStatusDialogShown: true,
      errorMessage: '',
    })
  );
  yield* put(patch(key, { isSendingFile: false }));
}

// ---------------------------------------------------------------------------
// GovQA — the only integration with an interactive (username/password) auth
// step. Credentials default to the org config; a send failure on cached creds
// reopens the auth dialog so the user can re-enter them.
// ---------------------------------------------------------------------------
export function* sendToGovQaSaga() {
  yield* takeEvery(SEND_TO_GOVQA, function* ({ payload }) {
    const key: IntegrationKey = 'govQa';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile, auditLog } = ctx;

    const config = yield* fetchConfig(
      govQAAPI.fetchIntegrationInfo,
      govQAAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      govQAAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    if (!config) {
      yield* failSend(key, 'Error fetching GovQA integration info');
      return;
    }
    const {
      BASEURL: baseUrl,
      AUTHKEY: authKey,
      ACTIVATIONKEY: activationKey,
      USERNAME: cfgUsername,
      PASSWORD: cfgPassword,
    } = config as Record<string, string>;
    if (
      !baseUrl ||
      !authKey ||
      !activationKey ||
      !cfgUsername ||
      !cfgPassword
    ) {
      yield* failSend(key, 'Missing GovQA integration config');
      return;
    }

    const uniqueId = session.vendorRequestIds.govQa;
    if (!uniqueId) {
      yield* put(patch(key, { isSendingFile: false }));
      return;
    }

    // Auth with the dialog credentials when provided, else the config ones.
    const auth: IntegrationAuth = payload ?? {};
    let sessionId: string;
    try {
      const authResponse = yield* call(govQAAPI.auth, {
        login: auth.username || cfgUsername,
        password: auth.password || cfgPassword,
        baseUrl,
        authKey,
        activationKey,
      });
      sessionId = authResponse.current_session_id;
      yield* put(patch(key, { sessionId }));
    } catch (errMsg) {
      yield* put(
        patch(key, {
          authErrorMessage:
            typeof errMsg === 'string' ? errMsg : 'Unknown error',
          isFormShown: true,
          isSendingFile: false,
        })
      );
      return;
    }

    try {
      yield* call(govQAAPI.sendFile, {
        sessionId,
        uniqueId,
        mediaFile,
        auditLog,
        activationKey,
        baseUrl,
      });
    } catch (_errMsg) {
      // Cached/auto creds failed — clear the session and reopen the auth
      // dialog so the user can re-enter; an explicit dialog send shows an error.
      yield* put(patch(key, { sessionId: null }));
      if (!payload) {
        yield* put(patch(key, { isFormShown: true, isSendingFile: false }));
      } else {
        yield* failSend(key, 'Error sending file');
      }
      return;
    }

    yield* succeedSend(key, 'Successfully sent file to GovQA.');
  });
}

// ---------------------------------------------------------------------------
// FOIAXpress — no auth; config provides only a base URL.
// ---------------------------------------------------------------------------
export function* sendToFOIAXpressSaga() {
  yield* takeEvery(SEND_TO_FOIAXPRESS, function* () {
    const key: IntegrationKey = 'foiaXpress';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile, auditLog } = ctx;

    const config = yield* fetchConfig(
      foiaXpressAPI.fetchIntegrationInfo,
      foiaXpressAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      foiaXpressAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    const { BASEURL: baseUrl } = (config ?? {}) as Record<string, string>;
    if (!config || !baseUrl) {
      yield* failSend(key, 'Missing FOIAXpress integration config');
      return;
    }
    const uniqueId = session.vendorRequestIds.foiaXpress;
    if (!uniqueId) {
      yield* put(patch(key, { isSendingFile: false }));
      return;
    }
    try {
      yield* call(foiaXpressAPI.sendFile, {
        uniqueId,
        mediaFile,
        auditLog,
        baseUrl,
      });
    } catch (_errMsg) {
      yield* failSend(key, 'Error sending file');
      return;
    }
    yield* succeedSend(key, 'Successfully sent file to FOIAXpress.');
  });
}

// ---------------------------------------------------------------------------
// Casepoint — no auth; config provides only a base URL.
// ---------------------------------------------------------------------------
export function* sendToCasepointSaga() {
  yield* takeEvery(SEND_TO_CASEPOINT, function* () {
    const key: IntegrationKey = 'casepoint';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile, auditLog } = ctx;

    const config = yield* fetchConfig(
      casepointAPI.fetchIntegrationInfo,
      casepointAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      casepointAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    const { BASEURL: baseUrl } = (config ?? {}) as Record<string, string>;
    if (!config || !baseUrl) {
      yield* failSend(key, 'Missing Casepoint integration config');
      return;
    }
    const uniqueId = session.vendorRequestIds.casepoint;
    if (!uniqueId) {
      yield* put(patch(key, { isSendingFile: false }));
      return;
    }
    try {
      yield* call(casepointAPI.sendFile, {
        uniqueId,
        mediaFile,
        auditLog,
        baseUrl,
      });
    } catch (_errMsg) {
      yield* failSend(key, 'Error sending file');
      return;
    }
    yield* succeedSend(key, 'Successfully sent file to Casepoint.');
  });
}

// ---------------------------------------------------------------------------
// Exterro — no auth; config provides a base URL and a signature (both required).
// ---------------------------------------------------------------------------
export function* sendToExterroSaga() {
  yield* takeEvery(SEND_TO_EXTERRO, function* () {
    const key: IntegrationKey = 'exterro';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile, auditLog } = ctx;

    const config = yield* fetchConfig(
      exterroAPI.fetchIntegrationInfo,
      exterroAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      exterroAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    const { BASEURL: baseUrl, SIGNATURE: signature } = (config ?? {}) as Record<
      string,
      string
    >;
    if (!config || !baseUrl || !signature) {
      yield* failSend(key, 'Missing Exterro integration config');
      return;
    }
    const uniqueId = session.vendorRequestIds.exterro;
    if (!uniqueId) {
      yield* put(patch(key, { isSendingFile: false }));
      return;
    }
    try {
      yield* call(exterroAPI.sendFile, {
        uniqueId,
        mediaFile,
        auditLog,
        signature,
        baseUrl,
      });
    } catch (_errMsg) {
      yield* failSend(key, 'Error sending file');
      return;
    }
    yield* succeedSend(key, 'Successfully sent file to Exterro.');
  });
}

// ---------------------------------------------------------------------------
// Nuix — no auth; signature required, base URL optional (falls back to the
// Ringtail endpoint). Nuix doesn't take an audit log.
// ---------------------------------------------------------------------------
const NUIX_FALLBACK_BASE_URL =
  'https://ringtaildr.us.nuix.com/ringtail-svc-portal/api/query';

export function* sendToNuixSaga() {
  yield* takeEvery(SEND_TO_NUIX, function* () {
    const key: IntegrationKey = 'nuix';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile } = ctx;

    const config = yield* fetchConfig(
      nuixAPI.fetchIntegrationInfo,
      nuixAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      nuixAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    const { BASEURL: baseUrl, SIGNATURE: signature } = (config ?? {}) as Record<
      string,
      string
    >;
    if (!config || !signature) {
      yield* failSend(key, 'Missing Nuix integration config');
      return;
    }
    const uniqueId = session.vendorRequestIds.nuix;
    if (!uniqueId) {
      yield* put(patch(key, { isSendingFile: false }));
      return;
    }
    try {
      yield* call(nuixAPI.sendFile, {
        uniqueId,
        mediaFile,
        signature,
        baseUrl: baseUrl || NUIX_FALLBACK_BASE_URL,
      });
    } catch (_errMsg) {
      yield* failSend(key, 'Error sending file');
      return;
    }
    yield* succeedSend(key, 'Successfully sent file to Nuix.');
  });
}

// ---------------------------------------------------------------------------
// Generic external integration (`redact-export`) — template-driven send with a
// custom label. Folder context is optional (empty when the document isn't in a
// case folder), mirroring the video flow's VE-23386 behavior.
// ---------------------------------------------------------------------------
export function* sendToExternalIntegrationSaga() {
  yield* takeEvery(SEND_TO_EXTERNAL_INTEGRATION, function* () {
    const key: IntegrationKey = 'externalIntegration';
    const ctx = yield* beginSend(key);
    if (!ctx) {
      return;
    }
    const { session, mediaFile, auditLog } = ctx;

    const integrationConfig = yield* fetchConfig(
      externalIntegrationAPI.fetchIntegrationInfo,
      externalIntegrationAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      externalIntegrationAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    if (!integrationConfig) {
      yield* failSend(key, 'Missing external integration config');
      return;
    }

    try {
      yield* call(externalIntegrationAPI.sendFile, {
        tdoId: session.tdoId,
        tdoName: session.documentName,
        folderId: session.folderId ?? '',
        folderName: session.folderName ?? '',
        // Send the human-readable status name, never the custom-status UUID
        // identity, to third-party integrations (VE-24546).
        reviewStatus:
          session.redactionStatusName ?? session.redactionStatus ?? '',
        // Documents have no transcript asset.
        transcriptionURL: '',
        mediaFile,
        auditLog,
        integrationConfig: integrationConfig as ExternalIntegrationConfig,
      });
    } catch (_errMsg) {
      yield* failSend(key, 'Error sending file');
      return;
    }

    yield* succeedSend(key, 'Successfully sent file to External Integration.');
  });
}

/**
 * Fetch the org's `redact-export` config on demand (dispatched by
 * RedactedFilePreview on mount) and stash it so the generic "Send to" button
 * can render its custom label. Best-effort — a failure just leaves the
 * default label.
 */
export function* fetchExternalIntegrationConfigSaga() {
  yield* takeEvery(FETCH_EXTERNAL_INTEGRATION_CONFIG, function* () {
    const config = yield* fetchConfig(
      externalIntegrationAPI.fetchIntegrationInfo,
      externalIntegrationAPI.FETCH_INTEGRATION_INFO_SUCCESS,
      externalIntegrationAPI.FETCH_INTEGRATION_INFO_FAILURE
    );
    if (config) {
      yield* put(
        patch('externalIntegration', {
          config: config as ExternalIntegrationConfig,
        })
      );
    }
  });
}
